import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Bell, LogOut, User as UserIcon, Sparkles } from "lucide-react";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Badge } from "@/components/ui/badge";

export const Navbar = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [notifs, setNotifs] = useState([]);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!user) return;
    const fetchN = async () => {
      try {
        const { data } = await api.get("/notifications");
        setNotifs(data);
      } catch (e) { console.error("Failed to fetch notifications:", e); }
    };
    fetchN();
    const t = setInterval(fetchN, 20000);
    return () => clearInterval(t);
  }, [user]);

  const unread = notifs.filter((n) => !n.read).length;

  const markAll = async () => {
    await api.post("/notifications/read-all");
    setNotifs((n) => n.map((x) => ({ ...x, read: true })));
  };

  const dashLink =
    user?.role === "provider" ? "/dashboard/provider" :
    user?.role === "admin" ? "/admin" : "/dashboard/customer";

  return (
    <header className="glass-nav sticky top-0 z-50">
      <div className="container-page flex items-center justify-between h-16">
        <Link to="/" className="flex items-center gap-2 font-display font-extrabold text-xl" data-testid="nav-logo">
          <span className="inline-block w-8 h-8 rounded-lg bg-gradient-to-br from-sky-400 to-sky-600 grid place-items-center text-white">
            <Sparkles className="w-4 h-4" />
          </span>
          <span className="text-slate-900">Pranidomu<span className="text-sky-600">.cz</span></span>
        </Link>

        <nav className="hidden md:flex items-center gap-7 text-sm text-slate-700">
          <Link to="/jak-to-funguje" className="hover:text-sky-600 transition-colors" data-testid="nav-how">Jak to funguje</Link>
          <Link to="/cenik" className="hover:text-sky-600 transition-colors" data-testid="nav-pricing">Ceník</Link>
          <Link to="/pradelny" className="hover:text-sky-600 transition-colors" data-testid="nav-providers">Prádelny</Link>
          <Link to="/pro-pradelny" className="hover:text-sky-600 transition-colors" data-testid="nav-for-providers">Pro prádelny</Link>
        </nav>

        <div className="flex items-center gap-3">
          {user ? (
            <>
              <DropdownMenu open={open} onOpenChange={setOpen}>
                <DropdownMenuTrigger asChild>
                  <button className="relative p-2 rounded-full hover:bg-slate-100" data-testid="nav-notifications-btn">
                    <Bell className="w-5 h-5 text-slate-700" />
                    {unread > 0 && (
                      <span className="absolute -top-0.5 -right-0.5 bg-sky-600 text-white text-[10px] font-semibold rounded-full w-4 h-4 grid place-items-center">
                        {unread}
                      </span>
                    )}
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-80 max-h-96 overflow-y-auto">
                  <div className="flex items-center justify-between px-2 py-1.5">
                    <DropdownMenuLabel className="px-0">Notifikace</DropdownMenuLabel>
                    {unread > 0 && (
                      <button onClick={markAll} className="text-xs text-sky-600 hover:underline" data-testid="nav-notifications-mark-all">
                        Označit jako přečtené
                      </button>
                    )}
                  </div>
                  <DropdownMenuSeparator />
                  {notifs.length === 0 ? (
                    <div className="p-4 text-sm text-slate-500 text-center">Žádné notifikace</div>
                  ) : (
                    notifs.slice(0, 12).map((n) => (
                      <DropdownMenuItem
                        key={n.id}
                        onClick={() => { setOpen(false); if (n.link) navigate(n.link); }}
                        className="flex flex-col items-start gap-0.5 py-2"
                        data-testid={`notif-${n.id}`}
                      >
                        <div className="flex items-center gap-2 w-full">
                          {!n.read && <span className="w-1.5 h-1.5 rounded-full bg-sky-600" />}
                          <span className="text-sm">{n.message}</span>
                        </div>
                        <span className="text-[10px] text-slate-400">{new Date(n.created_at).toLocaleString("cs-CZ")}</span>
                      </DropdownMenuItem>
                    ))
                  )}
                </DropdownMenuContent>
              </DropdownMenu>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="flex items-center gap-2 px-3 py-2 rounded-full hover:bg-slate-100" data-testid="nav-user-menu">
                    <span className="w-7 h-7 rounded-full bg-sky-100 text-sky-700 grid place-items-center text-sm font-bold">
                      {user.name?.[0]?.toUpperCase() || "U"}
                    </span>
                    <span className="hidden sm:inline text-sm text-slate-700">{user.name}</span>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel>
                    <div className="font-semibold">{user.name}</div>
                    <div className="text-xs text-slate-500 font-normal">{user.email}</div>
                    <Badge variant="secondary" className="mt-1 text-[10px]">
                      {user.role === "customer" ? "Zákazník" : user.role === "provider" ? "Prádelna" : "Admin"}
                    </Badge>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate(dashLink)} data-testid="nav-dashboard">
                    <UserIcon className="w-4 h-4 mr-2" /> Můj dashboard
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => { logout(); navigate("/"); }} data-testid="nav-logout">
                    <LogOut className="w-4 h-4 mr-2" /> Odhlásit se
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </>
          ) : (
            <>
              <Link to="/prihlaseni" className="text-sm text-slate-700 hover:text-sky-600" data-testid="nav-login">Přihlásit</Link>
              <Link to="/registrace" className="cta-primary text-sm py-2" data-testid="nav-register">Začít</Link>
            </>
          )}
        </div>
      </div>
    </header>
  );
};
